import { defineStore } from 'pinia'
import { ref } from 'vue'
import { salesLogin, generateWechatQrCode, checkWechatLoginStatus } from '@/api/sales'

/**
 * 销售人员信息接口定义
 */
export interface SalesInfo {
  userId: string
  refreshToken: string
  expiresTime: string
  accessToken: string
}

/**
 * 销售人员登录表单接口定义
 */
export interface SalesLoginForm {
  username: string
  password: string
}

/**
 * 微信二维码响应接口定义
 */
export interface WechatQrCodeResponse {
  qrCodeUrl: string
  scene: string
  expireTime: number
}

/**
 * 微信登录状态检查响应接口定义
 */
export interface WechatLoginStatusResponse {
  status: 'waiting' | 'success' | 'expired'
  userInfo?: any
}

/**
 * 销售人员状态管理
 * 功能：管理销售人员登录状态、用户信息、微信二维码生成等
 */
export const useSalesStore = defineStore(
  'sales',
  () => {
    // 状态
    const salesInfo = ref<SalesInfo | null>(null)
    const isLoggedIn = ref(false)

    // 动作
    /**
     * 销售人员登录
     * @param loginForm 登录表单数据
     * @returns Promise<SalesInfo>
     */
    const loginAction = async (loginForm: SalesLoginForm): Promise<SalesInfo> => {
      try {
        const response = await salesLogin(loginForm)
        salesInfo.value = response
        isLoggedIn.value = true

        // 保存token到localStorage
        localStorage.setItem('salesToken', response.accessToken)

        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    }

    /**
     * 生成微信登录二维码
     * @returns Promise<WechatQrCodeResponse>
     */
    const generateWechatQrCodeAction = async (): Promise<WechatQrCodeResponse> => {
      try {
        const response = await generateWechatQrCode()
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    }

    /**
     * 检查微信登录状态
     * @param scene 二维码场景值
     * @returns Promise<WechatLoginStatusResponse>
     */
    const checkWechatLoginStatusAction = async (
      scene: string
    ): Promise<WechatLoginStatusResponse> => {
      try {
        const response = await checkWechatLoginStatus(scene)
        return Promise.resolve(response)
      } catch (error) {
        return Promise.reject(error)
      }
    }

    /**
     * 销售人员登出
     */
    const logoutAction = () => {
      salesInfo.value = null
      isLoggedIn.value = false
      localStorage.removeItem('salesToken')
    }

    /**
     * 初始化销售人员信息（从localStorage恢复）
     */
    const initSalesInfo = () => {
      const token = localStorage.getItem('salesToken')
      if (token && salesInfo.value) {
        isLoggedIn.value = true
      }
    }

    /**
     * 重置状态
     */
    const resetState = () => {
      salesInfo.value = null
      isLoggedIn.value = false
    }

    return {
      // 状态
      salesInfo,
      isLoggedIn,

      // 动作
      loginAction,
      generateWechatQrCodeAction,
      checkWechatLoginStatusAction,
      logoutAction,
      initSalesInfo,
      resetState,
    }
  },
  {
    persist: true,
  }
)
